// 支持的语言
export const locales = ['zh', 'en'] as const;
// 语言类型
export type Locale = (typeof locales)[number];
// 默认语言
export const DEFAULT_LOCALE: Locale = 'zh';

export type LocaleOption = {
  value: Locale;
  label: string;
};

export type LanguageSwitchMeta = {
  targetLang: Locale;
  langLabel: 'CN' | 'EN';
};
// 语言选项
export const localeOptions: LocaleOption[] = [
  {
    value: locales[0],
    label: '中文',
  },
  {
    value: locales[1],
    label: 'EN',
  },
];

export function getLanguageSwitchMeta(
  currentLang: Locale
): LanguageSwitchMeta {
  // langLabel 展示「目标语言代号」：中文站显示 EN 表示可切英文，反之亦然。
  return currentLang === 'zh'
    ? {
        targetLang: 'en',
        langLabel: 'EN',
      }
    : {
        targetLang: 'zh',
        langLabel: 'CN',
      };
}

// 域名映射表：key为环境，value为该环境下的语言-域名映射
const hostMap = {
  test: {
    zh: `test.ssv.tencent.com`,
    en: `en-test.ssv.tencent.com`,
  },
  prod: {
    zh: 'ssv.tencent.com',
    en: `en.ssv.tencent.com`,
  },
};

type Envtype = keyof typeof hostMap | 'unknown';

const localDevHosts = ['localhost', '127.0.0.1', '0.0.0.0'];

const normalizeHostname = (host: string) => {
  // 浏览器 location.host 会包含端口，代理链路也可能带多个 host；这里统一收敛成裸域名用于环境判断。
  return (host || '').split(',')[0]?.trim().split(':')[0]?.toLowerCase() || '';
};

/**
 * 判断当前环境（测试/生产）
 * @param {string} host - 当前域名
 * @returns {string} 'test' / 'prod' / 'unknown'
 */
function getEnv(host: string) {
  const hostname = normalizeHostname(host);
  // 检查是否属于测试环境（匹配测试环境的两种域名）
  if (
    Object.values(hostMap.test).some((envHost) => envHost === hostname) ||
    localDevHosts.includes(hostname)
  ) {
    return 'test';
  }
  // 检查是否属于生产环境（匹配生产环境的两种域名）
  if (Object.values(hostMap.prod).some((envHost) => envHost === hostname)) {
    return 'prod';
  }
  return 'unknown';
}

/**
 * 生成切换语言后的新域名
 * @param {string} env - 环境（test/prod）
 * @param {string} targetLang - 目标语言（en/zh）
 * @returns {string} 新域名
 */
function getNewHost(env: Envtype, targetLang: Locale) {
  // 从映射表中直接获取对应域名（异常环境返回原域名）
  if (env === 'unknown') return window.location.host;
  return hostMap[env]?.[targetLang] || window.location.host;
}

/**
 * 切换语言（核心函数）
 * @param {string} targetLang - 目标语言（'en' 或 'zh'）
 * @returns {string} 切换后的完整URL
 */
export function switchLanguage(targetLang: Locale) {
  const currentUrl = new URL(window.location.href);
  const currentHost = currentUrl.host;
  const env = getEnv(currentHost); // 获取当前环境
  const newHost = getNewHost(env, targetLang); // 从映射表取新域名
  currentUrl.host = newHost;
  window.location.replace(currentUrl.toString());
  return currentUrl.toString();
}
